import { EMPTY, Observable, catchError, map, of, switchMap, takeWhile, timer } from 'rxjs';
import { RequestResult } from '../core/triggerbee/request-result';
import { DataPlatformVisitStatusRequest, DataPlatformVisitStatusResponse } from './data-platform.model';

const VISIT_STATUS_POLL_MS = 30_000;
// No status request when the last stored event is older than this.
const RECENT_ACTIVITY_WINDOW_MS = 60 * 60_000;
// Same window as Tracker's online count.
const ACTIVE_NOW_WINDOW_MS = 5 * 60_000;

export type VisitStatusFetch = (
  request: DataPlatformVisitStatusRequest,
) => Observable<RequestResult<DataPlatformVisitStatusResponse>>;

export function parseUtc(value: string): number {
  return new Date(value.endsWith('Z') ? value : `${value}Z`).getTime();
}

export function isRecentActivity(timestamp: string | null | undefined): boolean {
  return !!timestamp && Date.now() - parseUtc(timestamp) < RECENT_ACTIVITY_WINDOW_MS;
}

export function isActiveNow(timestamp: string | null | undefined, now: number): boolean {
  return !!timestamp && now - parseUtc(timestamp) < ACTIVE_NOW_WINDOW_MS;
}

export function isOpenVisit(status: DataPlatformVisitStatusResponse | null): status is DataPlatformVisitStatusResponse {
  return !!status && status.eventCount > 0 && !status.isFinalized;
}

export function hasVisitStatusChanged(
  previous: DataPlatformVisitStatusResponse,
  next: DataPlatformVisitStatusResponse,
): boolean {
  return (
    previous.isFinalized !== next.isFinalized ||
    previous.eventCount !== next.eventCount ||
    previous.lastEventAt !== next.lastEventAt
  );
}

/** Polls while the visit is open; completes on the first finalized, stale or failed reply. */
export function pollVisitStatus(
  fetch: VisitStatusFetch,
  visit: { uid: number; sessionId: number; endedAt: string },
): Observable<DataPlatformVisitStatusResponse | null> {
  if (!isRecentActivity(visit.endedAt)) {
    return EMPTY;
  }
  return timer(0, VISIT_STATUS_POLL_MS).pipe(
    switchMap(() =>
      fetch({ uid: visit.uid, sessionId: visit.sessionId }).pipe(
        map((result) => (result?.isPositive() ? result.data : null)),
        catchError(() => of(null)),
      ),
    ),
    takeWhile((status) => isOpenVisit(status) && isRecentActivity(status.lastEventAt), true),
  );
}
